import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TOUR_STEPS from "./tourSteps";

// A real Context (not sessionStorage) — the trigger button (Rules page) and the
// overlay (mounted once in Admin) are separate components and need to share one
// live state, not two independent copies that never notify each other.
const TourContext = createContext(null);

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const goToStep = useCallback(
    (index) => {
      if (index < 0 || index >= TOUR_STEPS.length) {
        setActive(false);
        setStepIndex(0);
        return;
      }
      setActive(true);
      setStepIndex(index);
      const targetRoute = TOUR_STEPS[index].route;
      if (targetRoute !== location.pathname) {
        navigate(targetRoute);
      }
    },
    [location.pathname, navigate]
  );

  const value = useMemo(
    () => ({
      active,
      stepIndex,
      totalSteps: TOUR_STEPS.length,
      isFirst: stepIndex === 0,
      isLast: stepIndex === TOUR_STEPS.length - 1,
      currentStep: active ? TOUR_STEPS[stepIndex] : null,
      start: () => goToStep(0),
      next: () => goToStep(stepIndex + 1),
      back: () => goToStep(stepIndex - 1),
      skip: () => {
        setActive(false);
        setStepIndex(0);
      },
    }),
    [active, stepIndex, goToStep]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export default function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour() must be used inside <TourProvider>");
  }
  return ctx;
}
